import { API_BASE_URL, authFetch } from './api';


// === Types ===

export interface AccountRef {
  pad_id: string;
  device_name: string | null;
  platform: string | null;
}

export interface AccountOverride {
  pad_id: string;
  persona_id?: string | null;
  persona_text?: string | null;
  task?: string | null;
  task_append?: string | null;
}

export interface Persona {
  id: string;
  name: string;
  persona_text: string;
  account_pad_ids: string[];
  accounts: AccountRef[];
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreatePersonaPayload {
  name: string;
  persona_text: string;
  account_pad_ids?: string[];
}

export interface UpdatePersonaPayload {
  name?: string;
  persona_text?: string;
  account_pad_ids?: string[];
}

export interface PromptTemplate {
  id: string;
  name: string;
  platform: string | null;
  task: string;
  persona_id: string | null;
  persona_name: string | null;
  persona_text: string | null;
  brand_id: string | null;
  brand_name: string | null;
  llm_model: string | null;
  comment_llm_model: string | null;
  max_steps: number;
  disable_proactive_search: boolean;
  capture_required: boolean;
  return_home_on_finish: boolean;
  max_comments_per_session: number | null;
  max_searches_per_session: number | null;
  max_follows_per_session: number | null;
  account_overrides: AccountOverride[];
  accounts: AccountRef[];
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplatePayload {
  name: string;
  platform?: string | null;
  task: string;
  persona_id?: string | null;
  persona_text?: string | null;
  brand_id?: string | null;
  llm_model?: string | null;
  comment_llm_model?: string | null;
  max_steps?: number;
  disable_proactive_search?: boolean;
  capture_required?: boolean;
  return_home_on_finish?: boolean;
  max_comments_per_session?: number | null;
  max_searches_per_session?: number | null;
  max_follows_per_session?: number | null;
  account_overrides?: AccountOverride[];
}

export type UpdateTemplatePayload = Partial<Omit<CreateTemplatePayload, 'name'>> & { name?: string };

// === API Functions ===

const BASE = `${API_BASE_URL}/prompts`;

// Personas
export async function listPersonas(padId?: string): Promise<Persona[]> {
  const qs = padId ? `?pad_id=${encodeURIComponent(padId)}` : '';
  const res = await authFetch(`${BASE}/personas${qs}`);
  return res.json();
}

export async function createPersona(payload: CreatePersonaPayload): Promise<Persona> {
  const res = await authFetch(`${BASE}/personas`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function updatePersona(id: string, payload: UpdatePersonaPayload): Promise<Persona> {
  const res = await authFetch(`${BASE}/personas/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function deletePersona(id: string): Promise<{ success: boolean; orphaned_templates: number }> {
  const res = await authFetch(`${BASE}/personas/${id}`, { method: 'DELETE' });
  return res.json();
}

export async function usePersona(id: string): Promise<void> {
  await authFetch(`${BASE}/personas/${id}/use`, { method: 'POST' });
}

// Prompt templates
export async function listTemplates(platform?: string, padId?: string): Promise<PromptTemplate[]> {
  const params = new URLSearchParams();
  if (platform) params.set('platform', platform);
  if (padId) params.set('pad_id', padId);
  const qs = params.toString() ? `?${params}` : '';
  const res = await authFetch(`${BASE}/templates${qs}`);
  return res.json();
}

export async function createTemplate(payload: CreateTemplatePayload): Promise<PromptTemplate> {
  const res = await authFetch(`${BASE}/templates`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function updateTemplate(id: string, payload: UpdateTemplatePayload): Promise<PromptTemplate> {
  const res = await authFetch(`${BASE}/templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function deleteTemplate(id: string): Promise<{ success: boolean }> {
  const res = await authFetch(`${BASE}/templates/${id}`, { method: 'DELETE' });
  return res.json();
}

export async function duplicateTemplate(id: string): Promise<PromptTemplate> {
  const res = await authFetch(`${BASE}/templates/${id}/duplicate`, { method: 'POST' });
  return res.json();
}

export async function useTemplate(id: string): Promise<void> {
  await authFetch(`${BASE}/templates/${id}/use`, { method: 'POST' });
}
